import type { TokenResponse } from './types'

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

type AuthListener = (session: TokenResponse | null) => void

/**
 * The access token lives only in memory (never in localStorage), so XSS can't lift a
 * long-lived credential. The refresh token is an httpOnly cookie scoped to /api/auth.
 */
let accessToken: string | null = null
let refreshing: Promise<TokenResponse | null> | null = null
const listeners = new Set<AuthListener>()

export function setSession(session: TokenResponse | null): void {
  accessToken = session?.access_token ?? null
  listeners.forEach((fn) => fn(session))
}

export function onSessionChange(fn: AuthListener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function hasAccessToken(): boolean {
  return accessToken !== null
}

/** Exchange the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<TokenResponse | null> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' })
    .then(async (res) => {
      const session = res.ok ? ((await res.json()) as TokenResponse) : null
      setSession(session)
      return session
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json()
    if (typeof body.detail === 'string') return body.detail
    if (Array.isArray(body.detail)) return body.detail.map((d: { msg: string }) => d.msg).join(', ')
    if (typeof body.error === 'string') return body.error
  } catch {
    /* non-JSON error body */
  }
  return res.status === 429 ? 'Too many requests, please slow down' : `Request failed (${res.status})`
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const send = () =>
    fetch(`/api${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      signal: init.signal,
      headers: {
        ...(init.body !== undefined && { 'Content-Type': 'application/json' }),
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    })

  let res = await send()
  // Access tokens are short-lived; transparently refresh once and retry.
  if (res.status === 401 && accessToken && !path.startsWith('/auth/')) {
    if (await refreshSession()) res = await send()
  }
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res))
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
